import React, { useState, useEffect, useRef } from "react";
import { Spinner, Alert, Button } from "reactstrap";

// ---------------------------------------------------------------------------
// src/pages/KPI/ReportFrame.js
//
// Safe embedded-report iframe. Used by the KPI hub to render Power BI
// "publish to web" reports (app.powerbi.com/view?r=...) inside the app.
//
// Security / safety:
//   • src is a controlled prop (string) — NEVER dangerouslySetInnerHTML.
//   • title is required for accessibility.
//   • allowFullScreen lets Power BI's own fullscreen button work.
//   • loading="lazy" so off-screen frames don't fetch until needed.
//   • A load timeout surfaces a graceful fallback if the provider refuses
//     to render in an iframe (X-Frame-Options / CSP frame-ancestors), with
//     an "Open in new tab" escape hatch so nothing is ever a dead end.
//
// Pure presentation — no business state, no API calls.
//
// Props
//   src           iframe URL (required)
//   title         accessible title (required)
//   height        CSS height (default "100%")
//   onOpenNewTab  optional () => void for the fallback button
//   timeoutMs     ms before assuming the embed was blocked (default 12000)
// ---------------------------------------------------------------------------

const ReportFrame = ({
    src,
    title,
    height = "100%",
    onOpenNewTab,
    timeoutMs = 12000,
}) => {
    const [state, setState] = useState("loading"); // loading | ready | blocked
    const timerRef = useRef(null);

    useEffect(() => {
        setState("loading");
        // If onLoad never fires (provider blocked framing, or it loaded an
        // error page we can't read cross-origin), fall back after a timeout.
        timerRef.current = window.setTimeout(() => {
            setState((s) => (s === "loading" ? "blocked" : s));
        }, timeoutMs);
        return () => {
            if (timerRef.current) window.clearTimeout(timerRef.current);
        };
    }, [src, timeoutMs]);

    const handleLoad = () => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
        setState("ready");
    };

    if (!src) {
        return (
            <Alert color="warning" className="m-3">
                <i className="ri-link-unlink-m me-1" />
                No report link is configured for this report yet.
            </Alert>
        );
    }

    return (
        <div style={{ position: "relative", width: "100%", height }}>
            {state === "loading" && (
                <div
                    className="d-flex flex-column align-items-center justify-content-center text-muted"
                    style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                        background: "var(--vz-card-bg, #fff)",
                    }}
                >
                    <Spinner color="primary" />
                    <span className="mt-2 fs-13">Loading report…</span>
                </div>
            )}

            {state === "blocked" && (
                <div
                    className="d-flex flex-column align-items-center justify-content-center text-center p-4"
                    style={{ position: "absolute", inset: 0, zIndex: 2 }}
                >
                    <i className="ri-shield-keyhole-line fs-36 text-muted mb-2" />
                    <h6 className="mb-1">This report can't be shown here</h6>
                    <p className="text-muted fs-13 mb-3" style={{ maxWidth: 420 }}>
                        The report provider blocked embedding, or it needs you to be
                        signed in. You can open it directly instead.
                    </p>
                    <Button
                        color="primary"
                        onClick={() =>
                            onOpenNewTab
                                ? onOpenNewTab()
                                : window.open(src, "_blank", "noopener,noreferrer")
                        }
                    >
                        <i className="ri-external-link-line me-1" />
                        Open in new tab
                    </Button>
                </div>
            )}

            <iframe
                title={title}
                src={src}
                onLoad={handleLoad}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer"
                style={{
                    width: "100%",
                    height: "100%",
                    border: 0,
                    display: "block",
                }}
            />
        </div>
    );
};

export default ReportFrame;
